import {
  Box,
  Stack,
  Text,
  HStack,
  Icon,
  VStack,
  Circle,
  ButtonGroup,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { Trans } from '@lingui/react/macro';
import { Check, X } from 'lucide-react';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';
import type { FormFieldOption } from '@duke-hq/shared-constants/assessments';
import { hasTiptapContent } from '@duke-hq/lib/dukeTiptapEditorHelpers';
import { isDefined } from '@duke-hq/lib';

import type { FormattedSlide } from './types';

type ChoiceFieldAnswerProps = {
  questionChoices: FormFieldOption[];
  answerValues: string[];
};

function ChoiceFieldAnswer({ questionChoices, answerValues }: ChoiceFieldAnswerProps) {
  const answerValuesSet = new Set(answerValues);

  return (
    <VStack spacing={2} align="stretch">
      {questionChoices.map(({ isCorrect, value, label }) => {
        const isSelected = isDefined(value) && answerValuesSet.has(value);

        return (
          <HStack
            key={value}
            justify="space-between"
            p={1}
            borderRadius="md"
            bg={isSelected ? 'bg.subtle' : undefined}
          >
            <HStack spacing={2}>
              {isSelected ? (
                <Circle size="4" bg="brand.500" color="white">
                  <Icon as={Check} boxSize={2.5} />
                </Circle>
              ) : (
                <Circle size="4" borderWidth="2px" borderColor="border.default" />
              )}
              <Text fontSize="sm">{label}</Text>
            </HStack>
            {isCorrect === true ? (
              <HStack spacing={1}>
                <Icon as={CheckIcon} color="success" boxSize={3} />
                <Text fontSize="xs" color="success">
                  <Trans>Correct option</Trans>
                </Text>
              </HStack>
            ) : null}
          </HStack>
        );
      })}
    </VStack>
  );
}

type FormAnswerProps = {
  slide: FormattedSlide;
  assessmentSubmissionId: number;
  assessedAt?: Date | string | null;
};

export function FormAnswer({ slide, assessmentSubmissionId, assessedAt }: FormAnswerProps) {
  const utils = trpcAdmin.useUtils();
  const updateStudentFormFieldAnswer =
    trpcAdmin.assessments.updateStudentFormFieldAnswer.useMutation({
      onSuccess: async () => {
        await utils.assessments.getById.refetch(assessmentSubmissionId);
      },
    });

  const handleFieldToggle = (fieldId: string, correct: boolean) => {
    updateStudentFormFieldAnswer.mutate({
      answerId: slide.answer?.originalAnswerId ?? 0,
      fieldId,
      correct,
      assessmentSubmissionId,
    });
  };

  const formFieldAnswers = slide.answer?.formFieldAnswers ?? [];
  const answerMap = new Map(formFieldAnswers.map((a) => [a.fieldId, a]));
  return (
    <VStack align="stretch" spacing={4}>
      {hasTiptapContent(slide.question?.description) ? (
        <EditorHTML content={slide.question.description} style={{ padding: 0 }} />
      ) : null}

      {(slide.sections ?? []).map((section) => (
        <Stack key={section.id} spacing={3}>
          {section.title ? (
            <Text fontSize="lg" fontWeight="medium" color="fg.emphasis">
              {section.title}
            </Text>
          ) : null}

          {hasTiptapContent(section.description) ? (
            <EditorHTML content={section.description} style={{ padding: 0 }} />
          ) : null}

          {section.fields.map((field) => {
            const { id, label, required, fieldType, options } = field;
            const answer = answerMap.get(id);

            if (answer === undefined) {
              return (
                <Box key={id}>
                  <Trans>No answer found</Trans>
                </Box>
              );
            }

            // Calculate loading state upfront
            const isUpdatingIncorrectButton =
              updateStudentFormFieldAnswer.variables?.fieldId === id &&
              updateStudentFormFieldAnswer.variables?.correct === false &&
              updateStudentFormFieldAnswer.isPending;

            const isUpdatingCorrectButton =
              updateStudentFormFieldAnswer.variables?.fieldId === id &&
              updateStudentFormFieldAnswer.variables?.correct === true &&
              updateStudentFormFieldAnswer.isPending;

            const hasAssessed = assessedAt != null;

            // Determine if interaction should be blocked.
            // NOTE: We avoid using `isDisabled` because disabling a focused button causes the browser
            // to lose focus and scroll the FormAnswer to the top.
            // Instead, we use `pointer-events: none` to block interaction while preserving focus.
            const shouldBlockInteraction =
              hasAssessed ||
              isUpdatingIncorrectButton ||
              isUpdatingCorrectButton ||
              updateStudentFormFieldAnswer.isPending;

            return (
              <Box key={id} borderTop="1px solid" borderColor="border.default" pt={3}>
                <HStack justify="space-between" align="flex-start" my={2}>
                  <Text fontWeight="normal" color="fg.default" fontSize="s">
                    {label}
                    {required === true ? (
                      <Text color="red.500" as="span" ml={1}>
                        *
                      </Text>
                    ) : null}
                  </Text>
                  <Box color="fg.muted" whiteSpace="nowrap" m={0}>
                    <ButtonGroup size="xs" isAttached>
                      <IconButton
                        aria-label="incorrect"
                        variant="unstyled"
                        border="2px solid"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderColor={answer.correct === false ? 'red.700' : 'border.default'}
                        backgroundColor={answer.correct === false ? 'red.600' : undefined}
                        borderRight={
                          answer.correct === true || answer.correct === null ? 'none' : undefined
                        }
                        roundedTopLeft={16}
                        roundedBottomLeft={16}
                        onClick={() => handleFieldToggle(id, false)}
                        opacity={shouldBlockInteraction ? 0.5 : 1}
                        cursor={shouldBlockInteraction ? 'not-allowed' : 'pointer'}
                        pointerEvents={shouldBlockInteraction ? 'none' : 'auto'}
                      >
                        {isUpdatingIncorrectButton ? (
                          <Spinner size="xs" color="red.500" />
                        ) : (
                          <Icon color="red.300" as={X} />
                        )}
                      </IconButton>
                      <IconButton
                        aria-label="correct"
                        variant="unstyled"
                        border="2px solid"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderColor={answer.correct === true ? 'green.700' : 'border.default'}
                        backgroundColor={answer.correct === true ? 'green.600' : undefined}
                        borderLeft={
                          answer.correct === false || answer.correct === null ? 'none' : undefined
                        }
                        roundedTopRight={16}
                        roundedBottomRight={16}
                        onClick={() => handleFieldToggle(id, true)}
                        opacity={shouldBlockInteraction ? 0.5 : 1}
                        cursor={shouldBlockInteraction ? 'not-allowed' : 'pointer'}
                        pointerEvents={shouldBlockInteraction ? 'none' : 'auto'}
                      >
                        {isUpdatingCorrectButton ? (
                          <Spinner size="xs" color="green.500" />
                        ) : (
                          <Icon
                            color={answer.correct === true ? 'bg.surface' : 'green.400'}
                            as={Check}
                          />
                        )}
                      </IconButton>
                    </ButtonGroup>
                  </Box>
                </HStack>

                <Text opacity={0.66} fontSize="sm" mb={1}>
                  {field.helperText}
                </Text>

                {field.fieldType === 'TEXT' ? (
                  <Box
                    p={3}
                    bg="bg.subtle"
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="md"
                    minH="40px"
                    maxH="150px"
                    whiteSpace="pre-wrap"
                    overflowY="auto"
                  >
                    {answer.textInputValue}
                  </Box>
                ) : fieldType === 'RADIO' || fieldType === 'SELECT' ? (
                  <ChoiceFieldAnswer
                    questionChoices={options ?? []}
                    answerValues={answer.selectedOptions ?? []}
                  />
                ) : null}
              </Box>
            );
          })}
        </Stack>
      ))}
    </VStack>
  );
}
